import { useCallback, useEffect, useMemo } from 'react';

import { CartInfoState } from '../CartInfoState';

import { useRootSelector } from 'rootStore';
import {
    ACHPaymentMethod,
    CheckPaymentMethod,
    CreditCardPaymentMethod,
    PaymentMethod,
    PaymentSubType,
    PaymentType,
    PaymentTypeFee,
    getFeeForPaymentMethod,
} from 'common/api/e-comm/payment-types';
import { useHomeDetails } from 'common/features/store/duck/home/utils/useHomeDetails';
import { usePaymentOptions } from 'common/hooks/usePaymentOptions';

export interface PaymentInfo {
    paymentSubType: PaymentSubType | null;
    paymentType: PaymentType;
    id: string | number;
}

export type PaymentInfoDescription = PaymentInfo & {
    description: string;
};

export const getPaymentMethod = (paymentInfo: PaymentInfo) => {
    if (paymentInfo.paymentType === PaymentType.Check) {
        return {
            paymentType: PaymentType.Check,
            achAccountId: null,
            creditCardId: null,
        } as CheckPaymentMethod;
    }

    if (paymentInfo.paymentType === PaymentType.BankAccount) {
        return {
            paymentSubType: null,
            paymentType: PaymentType.BankAccount,
            achAccountId: paymentInfo.id,
            creditCardId: null,
        } as ACHPaymentMethod;
    }

    if (paymentInfo.paymentType === PaymentType.CreditCard) {
        const creditCardId = paymentInfo.id ? parseInt(paymentInfo.id.toString(), 10) : null;

        return {
            paymentSubType: paymentInfo.paymentSubType,
            paymentType: PaymentType.CreditCard,
            achAccountId: null,
            creditCardId,
        } as CreditCardPaymentMethod;
    }

    return null;
};

export const getDefaultPaymentId = (paymentMethod: PaymentMethod | null): PaymentInfo => {
    if (paymentMethod) {
        if (paymentMethod.paymentType === PaymentType.BankAccount) {
            return {
                id: paymentMethod.achAccountId,
                paymentType: PaymentType.BankAccount,
                paymentSubType: null,
            };
        }

        if (paymentMethod.paymentType === PaymentType.CreditCard && paymentMethod.creditCardId) {
            return {
                id: paymentMethod.creditCardId.toString(),
                paymentType: PaymentType.CreditCard,
                paymentSubType: paymentMethod.paymentSubType || PaymentSubType.Credit,
            };
        }
    }

    return {
        paymentType: PaymentType.Check,
        id: PaymentType.Check,
        paymentSubType: null,
    };
};

export const isPaymentTypeValidForShipToHome = (paymentType: PaymentType | undefined) =>
    paymentType !== 'Check';

export const useCartPayment = (cartInfo: CartInfoState): CartInfoPayment => {
    const homeDetails = useHomeDetails();
    const { defaultPayment, defaultPaymentAsyncState } = useRootSelector((s) => s.store.account);
    const {
        loadPaymentOptionsData,
        paymentTypeFees,
        isLoading: isLoadingPaymentInfo,
        savedCreditCards,
        achAccount,
    } = usePaymentOptions();

    const { shipToHome, paymentMethodId } = cartInfo;

    useEffect(() => {
        if (homeDetails) {
            loadPaymentOptionsData();
        }
    }, [homeDetails, loadPaymentOptionsData]);

    const defaultPaymentMethodFee = useMemo(() => {
        return getFeeForPaymentMethod(paymentTypeFees, defaultPayment);
    }, [paymentTypeFees, defaultPayment]);

    const defaultPaymentType = defaultPayment?.paymentType;

    const validPayment = useMemo(() => {
        // if the current selected payment method is valid then it is ok.
        if (paymentMethodId) {
            return paymentMethodId;
        }

        // if the current selected payment is invalid or null check if the default method is valid.
        if (defaultPayment) {
            return getDefaultPaymentId(defaultPayment);
        }

        return;
    }, [paymentMethodId, shipToHome, defaultPayment]);

    let paymentFee: PaymentTypeFee | undefined;

    if (paymentTypeFees && validPayment) {
        // type casting from PaymentInfo -> PaymentMethod because they share the same
        // structure
        paymentFee =
            getFeeForPaymentMethod(paymentTypeFees, validPayment as unknown as PaymentMethod) ||
            undefined;
    }

    const getDefaultPaymentInfoDescription = useCallback(() => {
        if (!defaultPayment) {
            return;
        }

        switch (defaultPayment.paymentType) {
            case PaymentType.CreditCard: {
                if (defaultPayment.creditCardId) {
                    return {
                        id: defaultPayment.creditCardId,
                        paymentType: PaymentType.CreditCard,
                        paymentSubType: PaymentSubType.Credit,
                    };
                }
                return;
            }
            case PaymentType.BankAccount: {
                return {
                    id: defaultPayment.achAccountId,
                    paymentType: PaymentType.BankAccount,
                    paymentSubType: null,
                };
            }
            case PaymentType.Check: {
                return {
                    id: 'check',
                    paymentType: PaymentType.Check,
                    paymentSubType: null,
                };
            }
        }
    }, [defaultPayment]);

    const paymentAccount = useMemo<PaymentInfoDescription | undefined>(() => {
        const paymentInfoDescription = paymentMethodId || getDefaultPaymentInfoDescription();

        if (!paymentInfoDescription) {
            return;
        }

        if (paymentInfoDescription.paymentType === PaymentType.BankAccount) {
            return {
                ...paymentInfoDescription,
                description: achAccount?.accountNumber || '',
            };
        }

        if (paymentInfoDescription.paymentType === PaymentType.CreditCard) {
            if (paymentMethodId?.id === 'new-credit-card') {
                return {
                    ...paymentInfoDescription,
                    description: cartInfo.newCreditCard?.cardNumber.slice(-4) || '',
                };
            }

            if (savedCreditCards) {
                const creditCard = savedCreditCards.find(
                    (cc) =>
                        cc.id === paymentInfoDescription.id ||
                        cc.id.toString() === paymentInfoDescription.id
                );

                return {
                    ...paymentInfoDescription,
                    description: creditCard?.maskedCardNumber?.slice(-4) || '',
                };
            }
        }

        if (paymentInfoDescription.paymentType === PaymentType.Check) {
            return {
                ...paymentInfoDescription,
                description: '',
            };
        }
    }, [
        paymentMethodId,
        getDefaultPaymentInfoDescription,
        achAccount?.accountNumber,
        savedCreditCards,
        cartInfo.newCreditCard?.cardNumber,
    ]);

    return {
        isLoading: defaultPaymentAsyncState.loading || isLoadingPaymentInfo,
        defaultPaymentType,
        defaultPayment,
        defaultPaymentMethodFee,
        payment: validPayment,
        paymentFee,
        paymentAccount,
    };
};

export interface CartInfoPayment {
    isLoading: boolean;
    defaultPaymentType: PaymentType | undefined;
    defaultPayment: PaymentMethod | null;
    defaultPaymentMethodFee?: PaymentTypeFee | null;
    payment: PaymentInfo | undefined;
    paymentFee: PaymentTypeFee | undefined;
    paymentAccount?: PaymentInfoDescription;
}

export const emptyCartInfoPayment: CartInfoPayment = {
    isLoading: false,
    defaultPaymentType: undefined,
    defaultPayment: null,
    defaultPaymentMethodFee: undefined,
    payment: undefined,
    paymentFee: undefined,
    paymentAccount: undefined,
};
